/**
 * Step 4: Primary delivery message + scheduling (EXPRESS closed only).
 * Uses pipeline: classification → ETA → store status.
 */
(function (global) {
  const CHECKOUT_SEL = '[data-scheduled-delivery-checkout]';
  const SCHEDULE_SEL = '[data-skipp-schedule-delivery-btn]';
  const WRAP_SEL = '[data-scheduled-delivery-wrap]';
  const PANEL_SEL = '[data-scheduled-delivery-panel]';
  const DATE_SEL = '[data-scheduled-delivery-date]';
  const TIME_SEL = '[data-scheduled-delivery-time]';
  const STATUS_SEL = '[data-scheduled-delivery-status]';
  const BANNER_SEL = '[data-skipp-primary-delivery-banner]';
  const ICON_SEL = '[data-skipp-primary-delivery-icon]';
  const TEXT_SEL = '[data-skipp-primary-delivery-text]';
  const SUMMARY_SEL = '[data-skipp-schedule-summary]';
  const FIELDS_WRAP_SEL = '[data-skipp-schedule-fields-wrap]';
  const SUMMARY_DATE_SEL = '[data-skipp-schedule-summary-date]';
  const SUMMARY_TIME_SEL = '[data-skipp-schedule-summary-time]';
  const CHANGE_SEL = '[data-skipp-schedule-change]';
  const OPEN_PICKER_SEL = '[data-skipp-schedule-open-picker]';
  const CONFIRM_SEL = '[data-skipp-schedule-confirm]';
  const SHEET_SEL = '[data-skipp-schedule-sheet]';
  const SHEET_CLOSE_SEL = '[data-skipp-schedule-sheet-close]';
  const COLLAPSED_SEL = '[data-skipp-schedule-collapsed]';
  const COLLAPSED_DATE_SEL = '[data-skipp-schedule-collapsed-date]';
  const COLLAPSED_TIME_SEL = '[data-skipp-schedule-collapsed-time]';
  const DATE_PICKER_SEL = '[data-skipp-schedule-date-picker]';
  const TIME_PICKER_SEL = '[data-skipp-schedule-time-picker]';
  const ATTR_DATE = 'delivery_date';
  const ATTR_TIME = 'delivery_time';
  const DAYS_TO_SHOW = 5;

  const SPEED_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>';

  let lastPipelineResult = null;
  /** When true, scheduling date/time panel is visible after user clicks Schedule Delivery. */
  let schedulePanelExplicitOpen = false;
  /** Prevents pipeline / backdrop from closing the sheet right after open (mobile touchend). */
  let scheduleSheetLockUntil = 0;
  const SCHEDULE_SHEET_LOCK_MS = 650;
  let scheduleSheetUnlockTimer = 0;

  function armScheduleSheetLock() {
    schedulePanelExplicitOpen = true;
    scheduleSheetLockUntil = Date.now() + SCHEDULE_SHEET_LOCK_MS;
    window.clearTimeout(scheduleSheetUnlockTimer);
    scheduleSheetUnlockTimer = window.setTimeout(() => {
      queryAll(`cart-drawer ${SHEET_SEL}.is-opening`).forEach((sheet) => {
        sheet.classList.remove('is-opening');
      });
    }, SCHEDULE_SHEET_LOCK_MS);
  }

  function shouldKeepScheduleOpen() {
    return (
      schedulePanelExplicitOpen ||
      isScheduleSheetOpen() ||
      Date.now() < scheduleSheetLockUntil
    );
  }

  function canCloseScheduleSheetNow() {
    return Date.now() >= scheduleSheetLockUntil;
  }
  let scheduleState = {
    scheduledBrands: [],
    eligibleDates: [],
    selectedDate: '',
    selectedTime: '',
    savedDate: '',
    savedTime: '',
  };


  /** Minutes since midnight; used to intersect hours across multiple store schedules. */
  function parseTimeToMinutes(timeStr) {
    const s = String(timeStr || '0:00').trim();
    const p = s.split(':');
    const h = parseInt(p[0], 10);
    const m = parseInt(p[1] != null ? String(p[1]).replace(/\D/g, '').slice(0, 2) : '0', 10);
    const hh = Number.isFinite(h) ? h : 0;
    const mm = Number.isFinite(m) ? m : 0;
    return hh * 60 + mm;
  }

  function normalizeOffDays(value) {
    if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean);
    if (typeof value === 'string') {
      const t = value.trim();
      if (!t) return [];
      try {
        const p = JSON.parse(t);
        if (Array.isArray(p)) return normalizeOffDays(p);
      } catch {
        /* ignore */
      }
      return t.split(',').map((x) => x.trim()).filter(Boolean);
    }
    return [];
  }

  function normalizeSchedule(s) {
    const m = (v) => (v != null && typeof v === 'object' && 'value' in v ? v.value : v);
    const closedRaw = m(s?.is_closed_today) ?? s?.is_closed_today;
    const offRaw = m(s?.off_days) ?? s?.off_days;
    return {
      brand_name: String(m(s?.brand_name) ?? s?.brand_name ?? '').trim(),
      open_time: String(m(s?.open_time) ?? s?.open_time ?? '10:00').trim(),
      close_time: String(m(s?.close_time) ?? s?.close_time ?? '18:00').trim(),
      off_days: normalizeOffDays(offRaw),
      is_closed_today:
        closedRaw === true || closedRaw === 'true' || closedRaw === 1,
    };
  }

  function getDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function getWeekday(date) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  function isScheduleOffOnDate(schedule, date) {
    return (
      schedule.off_days.includes(getWeekday(date)) || schedule.off_days.includes(getDateKey(date))
    );
  }

  function isDayClosedByFlag(schedule, date, today) {
    if (getDateKey(date) !== getDateKey(today)) return false;
    return schedule.is_closed_today === true;
  }

  function buildEligibleDates(scheduledBrands) {
    const dates = [];
    const today = new Date();
    let add = 0;
    while (dates.length < DAYS_TO_SHOW && add < 60) {
      const date = new Date(today);
      date.setDate(today.getDate() + add);
      const blockedByOff = scheduledBrands.some(({ schedule }) => isScheduleOffOnDate(schedule, date));
      const blockedByTodayFlag = scheduledBrands.some(({ schedule }) =>
        isDayClosedByFlag(schedule, date, today)
      );
      if (!blockedByOff && !blockedByTodayFlag) {
        dates.push({
          value: getDateKey(date),
          label: date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }),
        });
      }
      add += 1;
    }
    return dates;
  }

  function generateSlots(open, close) {
    const slots = [];
    let start = parseInt(String(open || '0:00').split(':')[0], 10);
    const endHour = parseInt(String(close || '0:00').split(':')[0], 10);
    while (start < endHour) {
      const end = Math.min(start + 2, endHour);
      slots.push(`${String(start).padStart(2, '0')}:00–${String(end).padStart(2, '0')}:00`);
      start = end;
    }
    return slots;
  }

  /**
   * Slots when every scheduled (closed express) store is open: start at the latest opening,
   * end at the earliest closing. Uses minute precision on metaobject times, then 2h windows.
   */
  function buildTimeSlots(scheduledBrands, selectedDateStr) {
    const schedules = scheduledBrands.map(({ schedule }) => schedule);
    if (!schedules.length) return [];
    const openMin = Math.max(...schedules.map((sch) => parseTimeToMinutes(sch.open_time)));
    const closeMin = Math.min(...schedules.map((sch) => parseTimeToMinutes(sch.close_time)));
    if (!Number.isFinite(openMin) || !Number.isFinite(closeMin) || openMin >= closeMin) {
      return [];
    }
    const openHour = Math.ceil(openMin / 60);
    const closeHour = Math.floor(closeMin / 60);
    if (openHour >= closeHour) {
      return [];
    }
    let slots = generateSlots(
      `${String(openHour).padStart(2, '0')}:00`,
      `${String(closeHour).padStart(2, '0')}:00`
    );
    const todayKey = getDateKey(new Date());
    if (selectedDateStr && selectedDateStr === todayKey) {
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const minStartMin = Math.max(openMin, nowMin);
      const minStartHour = Math.ceil(minStartMin / 60);
      slots = slots.filter((slot) => {
        const slotStart = parseInt(slot.split(':')[0], 10);
        return slotStart >= minStartHour;
      });
    }
    return slots;
  }

  /**
   * Cart-native scheduling (not tied to a single pipeline row). For every line item: resolve
   * tags from API + DOM, match brand_schedule by vendor, apply same open/closed rules as Step 3.
   * Why the scheduler could hide before: derivePrimaryState only saw rows in cartWithStoreStatus;
   * if a vendor line was missing from ETA output or tags failed to resolve for that line, it never
   * got itemState SCHEDULING even though the cart still had a closed express SKU from that brand.
   */
  function analyzeMultiVendorScheduling(cart, rawSchedules, now) {
    const items = cart?.items;
    const cartVendors = [
      ...new Set(
        (items || [])
          .map((i) => String(i.vendor || i.product?.vendor || '').trim())
          .filter(Boolean)
      ),
    ];
    const normSchedules = (rawSchedules || []).map(normalizeSchedule).filter((s) => s.brand_name);
    const n = global.SkippDeliveryClassification?.normalize;
    const normalizeFn = typeof n === 'function' ? n : (str) => (str || '').trim().toLowerCase();
    const vendorNormSet = new Set(cartVendors.map((v) => normalizeFn(v)));
    const matchedSchedules = normSchedules.filter((s) => vendorNormSet.has(normalizeFn(s.brand_name)));

    const Store = global.SkippDeliveryStoreStatus;
    const D = global.SkippDeliveryClassification;
    const effectiveNow = now instanceof Date ? now : new Date();
    const domTags = D?.buildKeyToTagsFromDom?.() || {};

    const closedVendors = [];
    (items || []).forEach((item) => {
      const vendor = String(item.vendor || item.product?.vendor || '').trim();
      if (!vendor) return;
      const schedule = normSchedules.find((s) => normalizeFn(s.brand_name) === normalizeFn(vendor));
      if (!schedule) return;

      const enriched = D ? D.itemWithResolvedTags(item, domTags) : item;
      const userCity = D && typeof D.resolveUserCityString === 'function' ? D.resolveUserCityString() : '';
      const isExpressLine =
        D && typeof D.getDeliveryType === 'function'
          ? D.getDeliveryType(enriched, userCity) === D.EXPRESS
          : false;
      if (!isExpressLine) return;

      const isClosed =
        Store && typeof Store.isStoreClosed === 'function'
          ? Store.isStoreClosed(schedule, effectiveNow)
          : false;
      if (isClosed) closedVendors.push(vendor);
    });

    const closedUnique = [...new Set(closedVendors.map((v) => String(v).trim()).filter(Boolean))];
    const schedulingRequired = closedUnique.length > 0;

    console.log('MULTI VENDOR DEBUG:', {
      cartVendors,
      closedVendors: closedUnique,
      schedulingRequired,
    });

    return {
      cartVendors,
      matchedSchedules,
      closedVendors: closedUnique,
      schedulingRequired,
    };
  }

  function buildScheduledBrandsForClosedVendors(closedVendorNames, rawSchedules) {
    const norm = (rawSchedules || []).map(normalizeSchedule).filter((s) => s.brand_name);
    const n = global.SkippDeliveryClassification?.normalize;
    const normalizeFn = typeof n === 'function' ? n : (str) => (str || '').trim().toLowerCase();
    const seen = new Set();
    const out = [];
    (closedVendorNames || []).forEach((vendorRaw) => {
      const v = String(vendorRaw || '').trim();
      if (!v) return;
      if (seen.has(normalizeFn(v))) return;
      seen.add(normalizeFn(v));
      let schedule = norm.find((s) => normalizeFn(s.brand_name) === normalizeFn(v));
      if (!schedule) {
        schedule = normalizeSchedule({
          brand_name: v,
          open_time: '09:00',
          close_time: '21:00',
          off_days: [],
          is_closed_today: false,
        });
      }
      out.push({ brand: v, schedule });
    });
    return out;
  }

  function getClosedExpressScheduledBrands(itemStates, rawSchedules) {
    const n = global.SkippDeliveryClassification?.normalize;
    const normalizeFn = typeof n === 'function' ? n : (str) => (str || '').trim().toLowerCase();
    const norm = (rawSchedules || []).map(normalizeSchedule).filter((s) => s.brand_name);
    const rows = (itemStates || []).filter((i) => i.itemState === 'SCHEDULING');
    const seen = new Set();
    const out = [];
    rows.forEach((item) => {
      const v = String(item.vendor || '').trim();
      if (!v || seen.has(normalizeFn(v))) return;
      seen.add(normalizeFn(v));
      let schedule = norm.find((s) => normalizeFn(s.brand_name) === normalizeFn(v));
      if (!schedule) {
        schedule = normalizeSchedule({
          brand_name: v,
          open_time: '09:00',
          close_time: '21:00',
          off_days: [],
          is_closed_today: false,
        });
      }
      out.push({ brand: v, schedule });
    });
    return out;
  }

  function derivePrimaryState(cartWithStoreStatus) {
    const D = global.SkippDeliveryClassification;
    const EXPRESS = D?.EXPRESS || 'EXPRESS';
    const rows = cartWithStoreStatus || [];

    const itemStates = rows.map((row) => {
      /** Must match Step 1 getDeliveryType (same-city express only), not loose express+city_* tags. */
      const isExpress = row.deliveryType === EXPRESS;

      let itemState;
      if (!isExpress) {
        itemState = 'NEXT_DAY';
      } else if (row.storeStatus === 'CLOSED') {
        itemState = 'SCHEDULING';
      } else {
        itemState = 'EXPRESS_OPEN';
      }

      return { ...row, itemState, isExpress };
    });

    const hasScheduling = itemStates.some((i) => i.itemState === 'SCHEDULING');
    const hasExpressOpen = itemStates.some((i) => i.itemState === 'EXPRESS_OPEN');
    const hasOnlyNextDay =
      itemStates.length > 0 && itemStates.every((i) => i.itemState === 'NEXT_DAY');

    let finalState;
    if (hasScheduling) finalState = 'SCHEDULING';
    else if (hasExpressOpen) finalState = 'EXPRESS_OPEN';
    else finalState = 'NEXT_DAY';

    console.log('FINAL STATE DEBUG:', {
      hasScheduling,
      hasExpressOpen,
      hasOnlyNextDay,
      finalState,
    });

    return {
      primary: finalState,
      hasScheduling,
      hasExpressOpen,
      hasOnlyNextDay,
      hasClosedExpress: hasScheduling,
      hasOpenExpress: hasExpressOpen,
      hasExpress: hasScheduling || hasExpressOpen,
      itemStates,
    };
  }

  function expressOpenMessage(cartETA, cartETADisplay) {
    const E = global.SkippDeliveryEtaCalculation;
    const floor = E?.MIN_ETA_MINUTES != null ? E.MIN_ETA_MINUTES : 15;
    const fallbackMins = Math.max(floor, E?.FALLBACK_EXPRESS_MINUTES != null ? E.FALLBACK_EXPRESS_MINUTES : 45);
    const n = cartETA == null ? null : Number(cartETA);
    if (n != null && Number.isFinite(n)) {
      const mins = Math.max(floor, Math.ceil(n));
      if (mins <= 60) {
        return `It will be delivered in ${mins} mins`;
      }
      if (cartETADisplay) {
        return `It will be delivered in ${cartETADisplay}`;
      }
      return `It will be delivered in ${mins} mins`;
    }
    if (cartETADisplay) {
      return `It will be delivered in ${cartETADisplay}`;
    }
    return `It will be delivered in ${fallbackMins} mins`;
  }

  /** Single source of truth on the document root for CSS (suppress duplicate ETAs). */
  function syncDocumentFinalState(state) {
    const root = document.documentElement;
    if (!state) {
      root.removeAttribute('data-skipp-final-state');
      return;
    }
    root.setAttribute('data-skipp-final-state', state);
  }

  function formatSchedulingBannerMessage(closedVendorNames) {
    const names = [...new Set((closedVendorNames || []).map((v) => String(v || '').trim()).filter(Boolean))];
    if (names.length > 1) {
      return "Some of your items are from stores that are currently closed.\nPick a delivery time we'll take care of everything.";
    }
    const line2 = "Schedule your delivery and we'll make it worth the wait.";
    if (names.length === 1) {
      return `${names[0]} will be back soon ✨\n${line2}`;
    }
    return `The store will be back soon ✨\n${line2}`;
  }

  function readScheduleStorage() {
    try {
      return {
        date: String(localStorage.getItem(ATTR_DATE) || '').trim(),
        time: String(localStorage.getItem(ATTR_TIME) || '').trim(),
      };
    } catch {
      return { date: '', time: '' };
    }
  }

  function writeScheduleStorage(date, time) {
    try {
      if (date && time) {
        localStorage.setItem(ATTR_DATE, date);
        localStorage.setItem(ATTR_TIME, time);
      }
    } catch {
      /* ignore */
    }
  }

  function clearScheduleStorage() {
    try {
      localStorage.removeItem(ATTR_DATE);
      localStorage.removeItem(ATTR_TIME);
    } catch {
      /* ignore */
    }
  }

  function showSchedulePanel() {
    queryAll(WRAP_SEL).forEach((p) => {
      p.hidden = false;
    });
  }

  function openScheduleSheet() {
    armScheduleSheetLock();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const reveal = () => {
      if (!drawer.classList.contains('active')) return;
      queryAll(`cart-drawer ${SHEET_SEL}`).forEach((sheet) => {
        sheet.removeAttribute('hidden');
        sheet.setAttribute('aria-hidden', 'false');
        sheet.classList.add('is-open', 'is-opening');
      });
      drawer.classList.add('cart-drawer--schedule-open');
    };

    if (drawer.classList.contains('active')) {
      reveal();
      return;
    }

    let attempts = 0;
    const waitForActive = () => {
      if (drawer.classList.contains('active')) {
        reveal();
        return;
      }
      attempts += 1;
      if (attempts < 80) requestAnimationFrame(waitForActive);
    };
    requestAnimationFrame(waitForActive);
  }

  function isScheduleSheetOpen() {
    return Boolean(document.querySelector(`cart-drawer ${SHEET_SEL}.is-open`));
  }

  function closeScheduleSheet(immediate) {
    if (!immediate && !canCloseScheduleSheetNow()) return;
    schedulePanelExplicitOpen = false;
    scheduleSheetLockUntil = 0;
    const drawer = document.querySelector('cart-drawer');
    const sheets = drawer
      ? [...drawer.querySelectorAll(SHEET_SEL)]
      : queryAll(`cart-drawer ${SHEET_SEL}`);
    sheets.forEach((sheet) => {
      sheet.classList.remove('is-open', 'is-opening');
      sheet.setAttribute('aria-hidden', 'true');
      const finish = () => {
        if (!sheet.classList.contains('is-open')) {
          sheet.setAttribute('hidden', '');
        }
      };
      if (immediate || !drawer?.classList.contains('active')) {
        finish();
        return;
      }
      const panel = sheet.querySelector('.skipp-schedule-sheet__panel');
      if (panel) {
        panel.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(finish, 400);
      } else {
        finish();
      }
    });
    if (drawer) drawer.classList.remove('cart-drawer--schedule-open');
  }

  function resetScheduleOverlayState() {
    queryAll(SHEET_SEL).forEach((sheet) => {
      sheet.classList.remove('is-open');
      sheet.setAttribute('hidden', '');
      sheet.setAttribute('aria-hidden', 'true');
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.remove('cart-drawer--schedule-open');
  }

  function setScheduleViewMode(mode) {
    const showCollapsed = mode === 'summary';

    queryAll(SUMMARY_SEL).forEach((el) => {
      el.setAttribute('hidden', '');
    });

    queryAll(COLLAPSED_SEL).forEach((el) => {
      if (showCollapsed) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });

    queryAll(FIELDS_WRAP_SEL).forEach((el) => {
      el.setAttribute('hidden', '');
    });

    if (mode === 'edit') {
      openScheduleSheet();
    } else {
      closeScheduleSheet();
    }
  }

  function formatCollapsedDate(dateStr) {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return 'Select delivery slot';
    const parts = dateStr.split('-').map(Number);
    const dt = new Date(parts[0], parts[1] - 1, parts[2]);
    return dt.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  function updateScheduleSummaryDisplay() {
    const hasSaved = Boolean(scheduleState.savedDate && scheduleState.savedTime);
    const dateLabel = hasSaved
      ? formatCollapsedDate(scheduleState.savedDate)
      : 'Select delivery slot';
    const timeSlot = hasSaved ? scheduleState.savedTime || '' : '';
    queryAll(COLLAPSED_DATE_SEL).forEach((el) => {
      el.textContent = dateLabel;
    });
    queryAll(COLLAPSED_TIME_SEL).forEach((el) => {
      el.textContent = timeSlot;
      el.hidden = !timeSlot;
    });
    queryAll('[data-skipp-schedule-change]').forEach((el) => {
      el.textContent = hasSaved ? 'Change' : 'Choose';
    });
    queryAll(SUMMARY_DATE_SEL).forEach((el) => {
      el.textContent = dateLabel;
    });
    queryAll(SUMMARY_TIME_SEL).forEach((el) => {
      el.textContent = timeSlot;
    });
  }

  function renderPickerOption(listEl, value, label, groupName, selectedValue) {
    const li = document.createElement('li');
    const isSelected = value === selectedValue;
    const isDate = groupName === 'date';
    li.className = `skipp-schedule-picker__option${isDate ? ' skipp-schedule-picker__option--chip' : ''}${isSelected ? ' is-selected' : ''}`;
    if (!isDate) {
      const radio = document.createElement('span');
      radio.className = 'skipp-schedule-picker__radio';
      radio.setAttribute('aria-hidden', 'true');
      li.appendChild(radio);
    }
    const text = document.createElement('span');
    text.className = 'skipp-schedule-picker__text';
    text.textContent = label;
    li.appendChild(text);
    li.dataset.value = value;
    li.setAttribute('role', 'button');
    li.tabIndex = 0;
    li.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    const applySelection = () => {
      if (groupName === 'date') {
        scheduleState.selectedDate = value;
        scheduleState.selectedTime = '';
        queryAll(DATE_SEL).forEach((s) => {
          s.value = value;
        });
        renderScheduleUi();
      } else if (groupName === 'time') {
        scheduleState.selectedTime = value;
        queryAll(TIME_SEL).forEach((s) => {
          s.value = value;
        });
        renderPickerLists();
        updateScheduleConfirmState();
      }
    };
    li.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      applySelection();
    });
    li.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      applySelection();
    });
    listEl.appendChild(li);
  }

  function renderPickerLists() {
    const dateLists = queryAll(DATE_PICKER_SEL);
    const timeLists = queryAll(TIME_PICKER_SEL);
    const slots = buildTimeSlots(scheduleState.scheduledBrands, scheduleState.selectedDate);

    dateLists.forEach((list) => {
      list.innerHTML = '';
      scheduleState.eligibleDates.forEach((opt) => {
        renderPickerOption(list, opt.value, opt.label, 'date', scheduleState.selectedDate);
      });
    });

    timeLists.forEach((list) => {
      list.innerHTML = '';
      if (!slots.length) {
        const empty = document.createElement('li');
        empty.className = 'skipp-schedule-picker__text';
        empty.style.padding = '12px';
        empty.style.color = 'rgba(0,0,0,0.5)';
        empty.textContent = scheduleState.selectedDate
          ? 'No slots available'
          : 'Choose a date first';
        list.appendChild(empty);
        return;
      }
      slots.forEach((slot) => {
        renderPickerOption(list, slot, slot, 'time', scheduleState.selectedTime);
      });
    });
  }

  function queryAll(sel) {
    return Array.from(document.querySelectorAll(sel));
  }

  function setPrimaryCtas(showProceed, showSchedule) {
    queryAll(CHECKOUT_SEL).forEach((b) => {
      const hide = !showProceed;
      b.classList.toggle('skipp-cta-suppress', hide);
      b.toggleAttribute('hidden', hide);
      b.setAttribute('aria-hidden', hide ? 'true' : 'false');
    });
    queryAll(SCHEDULE_SEL).forEach((b) => {
      const hide = !showSchedule;
      b.classList.toggle('skipp-cta-suppress', hide);
      b.toggleAttribute('hidden', hide);
      b.setAttribute('aria-hidden', hide ? 'true' : 'false');
    });
  }

  function paintDeliveryBanner(banner, variant, showIcon, message) {
    const inner = banner.querySelector('[data-skipp-primary-delivery-inner]');
    const icon = banner.querySelector(ICON_SEL);
    const text = banner.querySelector(TEXT_SEL);
    if (inner) {
      inner.className = `delivery-banner ${variant} skipp-primary-delivery__row`;
    }
    if (icon) {
      icon.innerHTML = showIcon ? SPEED_ICON_SVG : '';
      icon.hidden = !showIcon;
    }
    if (text) {
      text.style.whiteSpace = variant === 'scheduling' ? 'pre-line' : '';
      text.textContent = message;
    }
  }

  function hidePerLineDeliveryRows() {
    /* Per-line copy lives in compact tags (skipp-delivery-per-line-note.js). */
  }

  function setMainCartStaticBannerHidden(hidden) {
    document.querySelectorAll('.delivery-time-message').forEach((el) => {
      el.style.display = hidden ? 'none' : '';
    });
  }

  function publishGlobals(primary, hasSavedSlot, closedVendors) {
    try {
      const scheduling = primary === 'SCHEDULING';
      window.__SKIPP_PRIMARY_DELIVERY_STATE__ = primary;
      window.__SKIPP_SCHEDULING_REQUIRED__ = scheduling && !hasSavedSlot;
      window.__SKIPP_HAS_SAVED_DELIVERY_SLOT__ = Boolean(hasSavedSlot);
      window.__SKIPP_SCHEDULING_CLOSED_VENDORS__ = closedVendors || [];
      document.dispatchEvent(
        new CustomEvent('skipp:scheduling-state', {
          detail: {
            primary,
            schedulingRequired: scheduling && !hasSavedSlot,
            hasSavedSlot,
            closedVendors: [...(window.__SKIPP_SCHEDULING_CLOSED_VENDORS__ || [])],
          },
        })
      );
    } catch {
      /* ignore */
    }
  }

  function syncSelects(selects, options, value) {
    selects.forEach((select) => {
      select.innerHTML = '';
      options.forEach((opt) => {
        const o = document.createElement('option');
        o.value = opt.value;
        o.textContent = opt.label;
        select.appendChild(o);
      });
      if (value && options.some((x) => x.value === value)) select.value = value;
      else if (options.length) select.value = options[0].value;
      else select.value = '';
    });
  }

  function syncTimeSelects(selects, slots, value) {
    selects.forEach((select) => {
      select.innerHTML = '';
      if (!slots.length) {
        const o = document.createElement('option');
        o.value = '';
        o.textContent = scheduleState.selectedDate ? 'No slots available' : 'Choose a date first';
        select.appendChild(o);
        select.disabled = true;
        select.value = '';
        return;
      }
      select.disabled = false;
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = 'Select time slot';
      select.appendChild(ph);
      slots.forEach((slot) => {
        const o = document.createElement('option');
        o.value = slot;
        o.textContent = slot;
        select.appendChild(o);
      });
      if (value && slots.includes(value)) select.value = value;
      else select.value = '';
    });
  }

  function readSelectValues() {
    const d = queryAll(DATE_SEL)[0];
    const t = queryAll(TIME_SEL)[0];
    scheduleState.selectedDate = d?.value || '';
    scheduleState.selectedTime = t?.value || '';
  }

  function hasSavedSelection() {
    if (!scheduleState.savedDate || !scheduleState.savedTime) return false;
    const elig =
      scheduleState.eligibleDates.length > 0
        ? scheduleState.eligibleDates
        : buildEligibleDates(scheduleState.scheduledBrands);
    const okDate = elig.some((x) => x.value === scheduleState.savedDate);
    const slots = buildTimeSlots(scheduleState.scheduledBrands, scheduleState.savedDate);
    return okDate && slots.includes(scheduleState.savedTime);
  }

  function setStatus(msg, isErr) {
    queryAll(STATUS_SEL).forEach((el) => {
      el.textContent = msg || '';
      el.classList.toggle('is-error', Boolean(isErr));
    });
  }

  function renderScheduleUi() {
    const dates = queryAll(DATE_SEL);
    const times = queryAll(TIME_SEL);

    scheduleState.eligibleDates = buildEligibleDates(scheduleState.scheduledBrands);

    if (scheduleState.savedDate && scheduleState.savedTime && !hasSavedSelection()) {
      scheduleState.savedDate = '';
      scheduleState.savedTime = '';
      scheduleState.selectedDate = '';
      scheduleState.selectedTime = '';
      clearScheduleStorage();
    }

    if (!scheduleState.selectedDate && scheduleState.eligibleDates.length) {
      scheduleState.selectedDate = scheduleState.eligibleDates[0].value;
    }

    syncSelects(dates, scheduleState.eligibleDates, scheduleState.selectedDate);
    readSelectValues();
    if (!scheduleState.eligibleDates.some((d) => d.value === scheduleState.selectedDate)) {
      scheduleState.selectedDate = scheduleState.eligibleDates[0]?.value || '';
      syncSelects(dates, scheduleState.eligibleDates, scheduleState.selectedDate);
      readSelectValues();
    }

    let slots = buildTimeSlots(scheduleState.scheduledBrands, scheduleState.selectedDate);
    if (!scheduleState.selectedTime || !slots.includes(scheduleState.selectedTime)) {
      scheduleState.selectedTime = '';
    }
    syncTimeSelects(times, slots, scheduleState.selectedTime);
    readSelectValues();

    if (!scheduleState.eligibleDates.length) {
      setStatus('No delivery slots available right now.', true);
    } else {
      setStatus('');
    }
    renderPickerLists();
    updateScheduleConfirmState();
  }

  function updateScheduleConfirmState() {
    readSelectValues();
    const slots = buildTimeSlots(scheduleState.scheduledBrands, scheduleState.selectedDate);
    const ok =
      scheduleState.eligibleDates.length > 0 &&
      Boolean(scheduleState.selectedDate) &&
      Boolean(scheduleState.selectedTime) &&
      slots.includes(scheduleState.selectedTime);
    queryAll(CONFIRM_SEL).forEach((btn) => {
      btn.disabled = !ok;
    });
  }

  function hideScheduleUi() {
    closeScheduleSheet(true);
    queryAll(WRAP_SEL).forEach((p) => {
      p.hidden = true;
    });
    setStatus('');
  }

  async function saveSchedule() {
    readSelectValues();
    if (!scheduleState.selectedDate || !scheduleState.selectedTime) return;

    queryAll(CHECKOUT_SEL).forEach((b) => {
      b.disabled = true;
    });
    queryAll(SCHEDULE_SEL).forEach((b) => {
      b.disabled = true;
    });
    queryAll(CONFIRM_SEL).forEach((b) => {
      b.disabled = true;
    });
    setStatus('Saving…');
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            [ATTR_DATE]: scheduleState.selectedDate,
            [ATTR_TIME]: scheduleState.selectedTime,
          },
        }),
      });
      if (!res.ok) throw new Error('save');
      const cart = await res.json();
      scheduleState.savedDate = scheduleState.selectedDate;
      scheduleState.savedTime = scheduleState.selectedTime;
      writeScheduleStorage(scheduleState.savedDate, scheduleState.savedTime);
      console.log('SCHEDULE SAVED:', {
        date: scheduleState.savedDate,
        time: scheduleState.savedTime,
      });
      setStatus('');
      showSchedulePanel();
      closeScheduleSheet();
      setScheduleViewMode('summary');
      updateScheduleSummaryDisplay();
      if (lastPipelineResult) {
        lastPipelineResult = { ...lastPipelineResult, cart };
        applyPipelineResult(lastPipelineResult);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cartData: cart } }));
    } catch {
      setStatus('Could not save. Try again.', true);
      if (lastPipelineResult) applyPipelineResult(lastPipelineResult);
    }
  }

  function updateBannersAndButtons(
    finalState,
    cartETA,
    cartETADisplay,
    cart,
    cartWithStoreStatus,
    schedulesRaw,
    itemStates,
    multiVendor
  ) {
    const items = cart?.items;
    const empty = !Array.isArray(items) || items.length === 0;

    const schedulable = (itemStates || []).filter((i) => i.itemState === 'SCHEDULING');
    const uniqueClosed = [...new Set(schedulable.map((i) => String(i.vendor || '').trim()).filter(Boolean))];
    const closedForUi =
      multiVendor?.closedVendors?.length > 0 ? multiVendor.closedVendors : uniqueClosed;

    attrsFromCart(cart);

    if (empty) {
      syncDocumentFinalState('');
      queryAll(BANNER_SEL).forEach((b) => {
        b.hidden = true;
      });
      setMainCartStaticBannerHidden(false);
      hideScheduleUi();
      clearScheduleStorage();
      schedulePanelExplicitOpen = false;
      setPrimaryCtas(true, false);
      queryAll(CHECKOUT_SEL).forEach((b) => {
        b.textContent = 'Proceed to Buy';
        b.disabled = false;
      });
      publishGlobals('NEXT_DAY', true, []);
      return;
    }

    syncDocumentFinalState(finalState);

    setMainCartStaticBannerHidden(true);
    hidePerLineDeliveryRows();

    let message = '';
    let showIcon = false;
    const bannerVariant = 'scheduling';

    if (finalState === 'SCHEDULING') {
      message = formatSchedulingBannerMessage(closedForUi);
      queryAll(BANNER_SEL).forEach((banner) => {
        banner.hidden = false;
        paintDeliveryBanner(banner, bannerVariant, showIcon, message);
      });
    } else {
      /** Express / next day: shipment strips only (no cart-level yellow bar). */
      queryAll(BANNER_SEL).forEach((banner) => {
        banner.hidden = true;
      });
    }

    const scheduling = finalState === 'SCHEDULING';
    let saved = false;
    if (scheduling) {
      if (multiVendor?.closedVendors?.length > 0) {
        scheduleState.scheduledBrands = buildScheduledBrandsForClosedVendors(
          multiVendor.closedVendors,
          schedulesRaw || global.SkippDeliveryEtaCalculation?.parseBrandSchedules?.() || []
        );
      } else {
        scheduleState.scheduledBrands = getClosedExpressScheduledBrands(
          itemStates,
          schedulesRaw || global.SkippDeliveryEtaCalculation?.parseBrandSchedules?.() || []
        );
      }
      scheduleState.eligibleDates = buildEligibleDates(scheduleState.scheduledBrands);
      saved = hasSavedSelection();
    }

    const hasSavedSlot = !scheduling || saved;

    publishGlobals(finalState, hasSavedSlot, closedForUi);

    if (!scheduling) {
      schedulePanelExplicitOpen = false;
      hideScheduleUi();
      setPrimaryCtas(true, false);
      queryAll(CHECKOUT_SEL).forEach((b) => {
        b.textContent = 'Proceed to Buy';
        b.disabled = false;
        b.removeAttribute('aria-expanded');
      });
      queryAll(SCHEDULE_SEL).forEach((b) => {
        b.disabled = false;
        b.removeAttribute('aria-expanded');
      });
      return;
    }

    if (saved) {
      showSchedulePanel();
      if (!shouldKeepScheduleOpen()) {
        setScheduleViewMode('summary');
        updateScheduleSummaryDisplay();
      }
      setPrimaryCtas(true, false);
      queryAll(CHECKOUT_SEL).forEach((b) => {
        b.textContent = 'Proceed to Buy';
        b.disabled = false;
        b.removeAttribute('aria-expanded');
      });
      queryAll(SCHEDULE_SEL).forEach((b) => {
        b.disabled = false;
        b.removeAttribute('aria-expanded');
      });
      return;
    }

    setPrimaryCtas(false, true);

    const distinctCartVendorCount = new Set(
      (items || [])
        .map((i) => String(i.vendor || i.product?.vendor || '').trim())
        .filter(Boolean)
    ).size;
    /** Multi-brand bags: keep slot picker visible (single-vendor still taps "Schedule Delivery" first). */
    const multiSchedulingUi =
      closedForUi.length > 1 ||
      scheduleState.scheduledBrands.length > 1 ||
      distinctCartVendorCount > 1;

    if (!shouldKeepScheduleOpen()) {
      hideScheduleUi();
    } else {
      showSchedulePanel();
    }
    try {
      renderScheduleUi();
    } catch {
      /* ignore */
    }

    queryAll(SCHEDULE_SEL).forEach((b) => {
      b.disabled = !scheduleState.eligibleDates.length && !multiSchedulingUi;
      b.setAttribute('aria-expanded', multiSchedulingUi ? 'true' : 'false');
    });
    queryAll(CHECKOUT_SEL).forEach((b) => {
      b.textContent = 'Proceed to Buy';
      b.disabled = false;
      b.removeAttribute('aria-expanded');
    });
  }

  function attrsFromCart(cart) {
    const a = cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
    let d = String(a[ATTR_DATE] || '').trim();
    let t = String(a[ATTR_TIME] || '').trim();
    const ls = readScheduleStorage();
    if (!d) d = ls.date;
    if (!t) t = ls.time;
    scheduleState.savedDate = d;
    scheduleState.savedTime = t;
    if (!scheduleState.selectedDate) scheduleState.selectedDate = d;
    if (!scheduleState.selectedTime) scheduleState.selectedTime = t;
  }

  function applyPipelineResult(result, cartOverride) {
    if (!result) return;
    const cart = cartOverride || result.cart;
    lastPipelineResult = { ...result, cart };
    const { cartWithStoreStatus, cartETA, cartETADisplay, schedules } = lastPipelineResult;

    const multiVendor = analyzeMultiVendorScheduling(cart, schedules);

    const flags = derivePrimaryState(cartWithStoreStatus);
    let finalState = flags.primary;
    if (multiVendor.schedulingRequired || flags.hasScheduling) {
      finalState = 'SCHEDULING';
    }

    if (finalState !== 'SCHEDULING' && !shouldKeepScheduleOpen()) {
      schedulePanelExplicitOpen = false;
    }

    console.log('FINAL UI STATE:', finalState);

    const etaForUi = finalState === 'EXPRESS_OPEN' ? cartETA : null;
    const etaDisplayForUi = finalState === 'EXPRESS_OPEN' ? cartETADisplay : null;

    updateBannersAndButtons(
      finalState,
      etaForUi,
      etaDisplayForUi,
      cart,
      cartWithStoreStatus,
      schedules,
      flags.itemStates,
      multiVendor
    );
  }

  let floGuardAttempts = 0;
  function installFloCheckoutGuard() {
    if (global.__SKIPP_FLO_CHECKOUT_GUARD__) return;
    const orig = global.handleFloCheckoutBtn;
    if (typeof orig !== 'function') {
      floGuardAttempts += 1;
      if (floGuardAttempts < 50) setTimeout(installFloCheckoutGuard, 200);
      return;
    }
    global.__SKIPP_FLO_CHECKOUT_GUARD__ = true;
    global.handleFloCheckoutBtn = function skippGuardedFloCheckout(...args) {
      if (
        global.__SKIPP_PRIMARY_DELIVERY_STATE__ === 'SCHEDULING' &&
        !global.__SKIPP_HAS_SAVED_DELIVERY_SLOT__
      ) {
        armScheduleSheetLock();
        showSchedulePanel();
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        } catch {
          /* ignore */
        }
        window.setTimeout(() => {
          setScheduleViewMode('edit');
          try {
            renderScheduleUi();
          } catch {
            /* ignore */
          }
        }, 0);
        return undefined;
      }
      return orig.apply(this, args);
    };
  }

  document.addEventListener(
    'click',
    (event) => {
      const sheetClose = event.target.closest(SHEET_CLOSE_SEL);
      if (sheetClose) {
        if (!canCloseScheduleSheetNow()) return;
        event.preventDefault();
        closeScheduleSheet();
        if (global.__SKIPP_HAS_SAVED_DELIVERY_SLOT__) {
          setScheduleViewMode('summary');
        }
        return;
      }

      const openPicker = event.target.closest(OPEN_PICKER_SEL);
      if (openPicker) {
        if (global.__SKIPP_PRIMARY_DELIVERY_STATE__ !== 'SCHEDULING') return;
        event.preventDefault();
        event.stopPropagation();
        armScheduleSheetLock();
        scheduleState.selectedDate = scheduleState.savedDate || scheduleState.selectedDate;
        scheduleState.selectedTime = scheduleState.savedTime || scheduleState.selectedTime;
        showSchedulePanel();
        window.setTimeout(() => {
          setScheduleViewMode('edit');
          try {
            renderScheduleUi();
          } catch {
            /* ignore */
          }
        }, 0);
        return;
      }

      const sched = event.target.closest(SCHEDULE_SEL);
      if (sched) {
        if (global.__SKIPP_PRIMARY_DELIVERY_STATE__ !== 'SCHEDULING') return;
        if (global.__SKIPP_HAS_SAVED_DELIVERY_SLOT__) return;
        if (sched.hidden || sched.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        armScheduleSheetLock();
        showSchedulePanel();
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        } catch {
          /* ignore */
        }
        window.setTimeout(() => {
          setScheduleViewMode('edit');
          try {
            renderScheduleUi();
          } catch {
            /* ignore */
          }
        }, 0);
        queryAll(SCHEDULE_SEL).forEach((b) => {
          b.setAttribute('aria-expanded', 'true');
        });
        return;
      }

      const confirmBtn = event.target.closest(CONFIRM_SEL);
      if (confirmBtn) {
        if (global.__SKIPP_PRIMARY_DELIVERY_STATE__ !== 'SCHEDULING') return;
        if (confirmBtn.disabled) return;
        event.preventDefault();
        saveSchedule();
        return;
      }

      const changeBtn = event.target.closest(CHANGE_SEL);
      if (changeBtn) {
        if (global.__SKIPP_PRIMARY_DELIVERY_STATE__ !== 'SCHEDULING') return;
        event.preventDefault();
        event.stopPropagation();
        armScheduleSheetLock();
        scheduleState.selectedDate = scheduleState.savedDate || scheduleState.selectedDate;
        scheduleState.selectedTime = scheduleState.savedTime || scheduleState.selectedTime;
        showSchedulePanel();
        window.setTimeout(() => {
          setScheduleViewMode('edit');
          try {
            renderScheduleUi();
          } catch {
            /* ignore */
          }
        }, 0);
        return;
      }

      const btn = event.target.closest(CHECKOUT_SEL);
      if (!btn) return;
      if (global.__SKIPP_PRIMARY_DELIVERY_STATE__ !== 'SCHEDULING') return;
      if (global.__SKIPP_HAS_SAVED_DELIVERY_SLOT__) return;
      event.preventDefault();
      armScheduleSheetLock();
      showSchedulePanel();
      try {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      } catch {
        /* ignore */
      }
      window.setTimeout(() => {
        setScheduleViewMode('edit');
        try {
          renderScheduleUi();
        } catch {
          /* ignore */
        }
      }, 0);
    },
    false
  );

  document.addEventListener('change', (event) => {
    const t = event.target;
    if (!(t instanceof HTMLSelectElement)) return;
    if (t.matches(DATE_SEL)) {
      scheduleState.selectedDate = t.value;
      scheduleState.selectedTime = '';
      queryAll(DATE_SEL).forEach((s) => {
        if (s !== t) s.value = scheduleState.selectedDate;
      });
      renderScheduleUi();
      return;
    }
    if (t.matches(TIME_SEL)) {
      scheduleState.selectedTime = t.value;
      queryAll(TIME_SEL).forEach((s) => {
        if (s !== t) s.value = scheduleState.selectedTime;
      });
      updateScheduleConfirmState();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!document.querySelector(`cart-drawer ${SHEET_SEL}.is-open`)) return;
    event.preventDefault();
    closeScheduleSheet();
    if (global.__SKIPP_HAS_SAVED_DELIVERY_SLOT__) {
      setScheduleViewMode('summary');
    }
  });

  document.addEventListener('skipp:delivery-pipeline-complete', (e) => {
    const d = e.detail;
    if (d) applyPipelineResult(d);
  });

  global.SkippDeliveryCartUx = {
    derivePrimaryState,
    applyPipelineResult,
    expressOpenMessage,
    analyzeMultiVendorScheduling,
  };

  document.addEventListener('skipp:cart-drawer-closed', () => {
    closeScheduleSheet(true);
  });

  function installScheduleSheetInteraction() {
    if (global.__SKIPP_SCHEDULE_SHEET_UI__) return;
    global.__SKIPP_SCHEDULE_SHEET_UI__ = true;

    document.addEventListener(
      'click',
      (event) => {
        const sheet = event.target.closest(SHEET_SEL);
        if (!sheet?.classList.contains('is-open') || !sheet.closest('cart-drawer')) return;
        const panel = event.target.closest('.skipp-schedule-sheet__panel');
        if (panel) return;
        if (!canCloseScheduleSheetNow()) return;
        event.preventDefault();
        closeScheduleSheet();
      },
      false
    );

    document.addEventListener(
      'click',
      (event) => {
        const opt = event.target.closest('.skipp-schedule-picker__option');
        const sheet = opt?.closest(SHEET_SEL);
        if (!opt || !sheet?.classList.contains('is-open') || !sheet.closest('cart-drawer')) return;
        event.preventDefault();
        event.stopPropagation();
        const list = opt.closest('[data-skipp-schedule-date-picker], [data-skipp-schedule-time-picker]');
        const value = opt.dataset.value || '';
        if (!value || !list) return;
        if (list.matches(DATE_PICKER_SEL)) {
          scheduleState.selectedDate = value;
          scheduleState.selectedTime = '';
          queryAll(DATE_SEL).forEach((s) => {
            s.value = value;
          });
          renderScheduleUi();
          return;
        }
        if (list.matches(TIME_PICKER_SEL)) {
          scheduleState.selectedTime = value;
          queryAll(TIME_SEL).forEach((s) => {
            s.value = value;
          });
          renderPickerLists();
          updateScheduleConfirmState();
        }
      },
      true
    );
  }

  document.addEventListener('DOMContentLoaded', () => {
    resetScheduleOverlayState();
    installFloCheckoutGuard();
    installScheduleSheetInteraction();
  });
  global.addEventListener('load', () => {
    resetScheduleOverlayState();
    installFloCheckoutGuard();
    installScheduleSheetInteraction();
  });
})(typeof window !== 'undefined' ? window : globalThis);
